import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SpecimenLabel } from "./SpecimenLabel";
import { Copy, Check } from "lucide-react";
import PraiseLogo from "@/components/PraiseLogo";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const LiveExtractionDemo = () => {
  const [input, setInput] = useState("");
  const [isExtracting, setIsExtracting] = useState(false);
  const [outputs, setOutputs] = useState<{ type: string; content: string }[] | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [demoUsed, setDemoUsed] = useState(false);

  const handleExtract = async () => {
    if (!input.trim()) return;

    if (demoUsed) {
      toast.info("Sign up for free to get 3 more transformations!");
      return;
    }

    setIsExtracting(true);

    try {
      const { data, error } = await supabase.functions.invoke("transform-testimonial", {
        body: {
          testimonial: input,
          clientName: "Demo User",
          brandName: "Your Brand",
          outputTypes: ["linkedin_post", "one_liner", "email_template"],
          isDemo: true,
        },
      });

      if (error) throw error;

      if (data?.transformations) {
        const result: { type: string; content: string }[] = [];
        const t = data.transformations;
        if (t.linkedin_post) result.push({ type: "LinkedIn Post", content: t.linkedin_post });
        if (t.one_liner) result.push({ type: "One-Liner", content: t.one_liner });
        if (t.email_template) result.push({ type: "Email Template", content: typeof t.email_template === "string" ? t.email_template : JSON.stringify(t.email_template) });
        if (result.length === 0 && t.raw) result.push({ type: "Output", content: t.raw });
        setOutputs(result);
        setDemoUsed(true);
      }
    } catch (error) {
      console.error("Demo error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsExtracting(false);
    }
  };

  const handleCopy = (index: number, content: string) => {
    navigator.clipboard.writeText(content);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleReset = () => {
    setInput("");
    setOutputs(null);
  };

  return (
    <div className="grid md:grid-cols-2 gap-8 md:gap-12 relative">
      {/* Input side */}
      <div className="relative">
        <SpecimenLabel text="INPUT" className="mb-3 block" />
        <div className="relative">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Paste any client testimonial, review, or feedback..."
            className="min-h-[200px] ink-border bg-background resize-none text-base"
            disabled={isExtracting}
          />
          {!outputs && (
            <Button
              onClick={handleExtract}
              disabled={!input.trim() || isExtracting}
              className="mt-4 gradient-brand text-white"
            >
              {isExtracting ? (
                <>
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
                    <PraiseLogo size={16} className="mr-2" />
                  </motion.div>
                  Extracting...
                </>
              ) : (
                <>
                  <PraiseLogo size={16} className="mr-2" />
                  Extract Now
                </>
              )}
            </Button>
          )}
          {outputs && (
            <Button onClick={handleReset} variant="outline" className="mt-4">Try Another</Button>
          )}
        </div>
      </div>

      {/* Thread connector */}
      <motion.div
        className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12"
        initial={{ opacity: 0 }}
        animate={{ opacity: isExtracting || outputs ? 1 : 0.3 }}
      >
        <motion.div
          className="h-px w-full bg-brand-600/60"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: isExtracting || outputs ? 1 : 0 }}
          transition={{ duration: 0.4 }}
          style={{ transformOrigin: "left" }}
        />
        {isExtracting && (
          <motion.div className="absolute top-0 left-0 h-px w-4 bg-brand-500" animate={{ x: [0, 48, 0] }} transition={{ duration: 0.8, repeat: Infinity }} />
        )}
      </motion.div>

      {/* Output side */}
      <div>
        <SpecimenLabel text="OUTPUT" className="mb-3 block" />
        <AnimatePresence mode="wait">
          {!outputs && !isExtracting && (
            <motion.div key="placeholder" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="min-h-[200px] border border-dashed border-border rounded-sm p-6 flex items-center justify-center">
              <p className="text-muted-foreground text-center">Your transformed content will appear here</p>
            </motion.div>
          )}
          {isExtracting && (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="min-h-[200px] border border-brand-200 rounded-sm p-6 flex items-center justify-center">
              <div className="text-center">
                <motion.div className="w-8 h-8 rounded-full border-2 border-brand-600/30 border-t-brand-600 mx-auto mb-3" animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} />
                <p className="text-muted-foreground text-sm">Analyzing sentiment...</p>
              </div>
            </motion.div>
          )}
          {outputs && (
            <motion.div key="outputs" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
              {outputs.map((output, index) => (
                <motion.div key={output.type} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.15 }} className="border border-brand-200 rounded-sm p-4 bg-background relative group">
                  <div className="flex items-center justify-between mb-2">
                    <SpecimenLabel text={output.type} />
                    <button onClick={() => handleCopy(index, output.content)} className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 hover:bg-muted rounded">
                      {copiedIndex === index ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5 text-muted-foreground" />}
                    </button>
                  </div>
                  <p className="text-sm text-foreground whitespace-pre-line leading-relaxed">{output.content}</p>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
